import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, verifyRegistration } from "@/lib/api";
import useAccessibleDialog from "@/hooks/useAccessibleDialog";
import { LenisEmblem } from "@/components/Logo";

export default function AuthModal() {
  const { showAuth, setShowAuth, authMode, setAuthMode, login, register, loginWithGoogle } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [waitSeconds, setWaitSeconds] = useState(0);
  const requestEpoch = useRef(0);
  useEffect(() => {
    if (!waitSeconds) return undefined;
    const timer = setTimeout(() => setWaitSeconds(value => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [waitSeconds]);
  useEffect(() => {
    if (!showAuth) {
      requestEpoch.current += 1;
      setLoading(false);
      setChallenge(null); setCode(""); setError(""); setNotice("");
      setForm({name: "", email: "", password: ""});
    }
  }, [showAuth]);

  const dialog = useRef(null);
  useAccessibleDialog(dialog, showAuth, () => setShowAuth(false));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isLogin = authMode === "login";

  const submit = async (e) => {
    e.preventDefault();
    if (loading || waitSeconds) return;
    const epoch = requestEpoch.current;
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        if (epoch !== requestEpoch.current) return;
        toast.success("Welcome back.");
        setShowAuth(false);
      } else if (challenge) {
        const result = await verifyRegistration({registration_id: challenge, code});
        if (epoch !== requestEpoch.current) return;
        setNotice(result.message); setChallenge(null); setCode(""); setAuthMode("login");
      } else {
        const result = await register(form.name, form.email, form.password);
        if (epoch !== requestEpoch.current) return;
        setChallenge(result.registration_id); setNotice(result.message);
      }
      setForm(previous => ({...previous, password: ""}));
    } catch (err) {
      if (epoch !== requestEpoch.current) return;
      setError(formatApiError(err.response?.data?.detail) || err.message);
      if (err.response?.status === 429) {
        const seconds = Number(err.response.headers?.["retry-after"]);
        setWaitSeconds(Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 60);
      }
    } finally {
      if (epoch === requestEpoch.current) setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {showAuth && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-dialog-backdrop aria-hidden="true" onClick={() => setShowAuth(false)} className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
            ref={dialog} role="dialog" aria-modal="true" aria-label={isLogin ? "Sign in to LENIS" : "Create a LENIS account"} tabIndex={-1} data-testid="auth-modal"
            className="fixed left-1/2 top-1/2 z-[90] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-white/10 bg-[#161616] p-8"
          >
            <button onClick={() => setShowAuth(false)} aria-label="Close account dialog" data-testid="auth-close"
              className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-[#F7F6F0] hover:text-white">
              <X size={17} />
            </button>
            <div className="flex flex-col items-center text-center">
              <LenisEmblem size={40} />
              <h2 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-white">
                {isLogin ? "Sign in" : challenge ? "Confirm your email" : "Create account"}
              </h2>
              <p className="mt-1 text-sm text-[#A1A1A6]">
                {isLogin ? "Track orders & manage deliveries." : "Save your details & manage Subscribe & Save."}
              </p>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-3">
              {notice && <p role="status" className="text-sm text-[#D6A921]">{notice}</p>}
              {!challenge && !isLogin && (
                <input aria-label="Full name" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" data-testid="auth-name"
                  className="w-full rounded-xl border border-white/10 bg-[#080806] px-4 py-3 text-sm text-white placeholder:text-[#A1A1A6] outline-none focus:border-[#D6A921]" />
              )}
              {!challenge && <><input required aria-label="Email address" autoComplete="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" data-testid="auth-email"
                className="w-full rounded-xl border border-white/10 bg-[#080806] px-4 py-3 text-sm text-white placeholder:text-[#A1A1A6] outline-none focus:border-[#D6A921]" />
              <input required minLength={isLogin ? undefined : 6} maxLength={isLogin ? 256 : 72} aria-label="Password" autoComplete={isLogin ? "current-password" : "new-password"} type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Password" data-testid="auth-password"
                className="w-full rounded-xl border border-white/10 bg-[#080806] px-4 py-3 text-sm text-white placeholder:text-[#A1A1A6] outline-none focus:border-[#D6A921]" /></>}
              {challenge && <label className="block text-sm">Six-digit email code
                <input required autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={event => setCode(event.target.value.replace(/\D/g, ""))} data-testid="auth-code" className="mt-2 w-full rounded-xl border border-white/20 bg-[#080806] px-4 py-3 text-white" />
              </label>}
              {error && <p role="alert" className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
              <button type="submit" disabled={loading || waitSeconds > 0} data-testid="auth-submit"
                className="w-full rounded-full bg-[#D6A921] py-3.5 text-sm font-bold uppercase tracking-wide text-[#080806] hover:bg-white transition-colors disabled:opacity-60">
                {loading ? "Please wait…" : waitSeconds ? `Try again in ${waitSeconds}s` : isLogin ? "Sign in" : challenge ? "Confirm email" : "Create account"}
              </button>
              {challenge && <button type="button" disabled={loading} onClick={() => {setChallenge(null); setCode(""); setNotice(""); setError("");}} className="min-h-[44px] text-sm text-[#D6A921]">Use a different email or request another code</button>}
            </form>

            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-xs uppercase tracking-widest text-[#A1A1A6]">or</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <button onClick={loginWithGoogle} data-testid="auth-google"
              className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white py-3.5 text-sm font-bold text-[#080806] hover:bg-slate-100 transition-colors">
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 4.5 29.5 2.5 24 2.5 12.1 2.5 2.5 12.1 2.5 24S12.1 45.5 24 45.5 45.5 35.9 45.5 24c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M5.3 14.7l6.6 4.8C13.6 15.1 18.4 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 4.5 29.5 2.5 24 2.5 16 2.5 9.1 7 5.3 14.7z"/>
                <path fill="#4CAF50" d="M24 45.5c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.5-4.6 2.4-7.5 2.4-5.2 0-9.6-3.3-11.2-8l-6.6 5.1C9 41 15.9 45.5 24 45.5z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5c-.5.4 6.8-4.9 6.8-15 0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>

            <p className="mt-5 text-center text-sm text-[#A1A1A6]">
              {isLogin ? "New to LENIS Nutrition?" : "Already have an account?"}{" "}
              <button disabled={loading} onClick={() => { setAuthMode(isLogin ? "register" : "login"); setError(""); setNotice(""); setChallenge(null); setCode(""); }} data-testid="auth-toggle"
                className="font-semibold text-[#D6A921] hover:underline">
                {isLogin ? "Create account" : "Sign in"}
              </button>
            </p>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}


